/* Presentation-only studio masks. Explicit upload opt-ins share the unchanged
   product analyzer; original URLs, RGB and product data are never replaced. */
(() => {
  'use strict';
  const MAX_EDGE = 1000;
  const CACHE_ENTRIES = 32;
  const CACHE_BYTES = 6000000;
  const BRAND_MASK_ASSETS = Object.freeze({
    'cn-logo-brand-philips-lite-on-digital-solutions-plds.png': 'af05e1aaecf1503b5c51e365fc43895043d579efe4a44ec355ea2ff4c3623c82',
    'cn-logo-brand-union-memory.jpg': '14101cefa2ed340fa07e3aef61289fd5157ea9cce20a92c6335331220248ee6b'
  });
  const PRODUCT_SELECTORS = [
    '.cn-product-card .card-image img.cn-card-primary-image',
    '.cn-product-card .card-image img.cn-card-secondary-image',
    '.product-card-wrapper .card__media img',
    '.cn-hero.hero img.hero-device--product',
    '.cn-brand-hero-image[data-image-source="product"] > img',
    '.cn-native-product .product-media-container.media-type-image .product__media img',
    '.cn-native-product .thumbnail-list .thumbnail img',
    '.cn-media-modal .product-media-modal__content > img[data-media-id]',
    '.cn-media-modal [data-cn-modal-thumbnails] img',
    '.cn-theme img.cart-item__image',
    '.cn-suggestion-product .cn-suggestion-image img',
    'img.predictive-search__image',
    '.cn-saved-page .cn-compare-product img',
    '.cn-comparison-item-image img',
    '.cn-comparison-suggestion > img',
    '.accessory-feature-image img'
  ].join(',');
  const EXCLUDED = [
    '[data-cn-product-image="false"]', '[data-image-source="custom"]',
    '[data-image-source="logo"]', '.manufacturer-logo', '.cn-brand-mark',
    '.cn-brand-art-mark', '.cn-brand-tile-mark', '.cn-hero-brand-strip',
    '.cn-brand-hero-logo', '.brand-logo', '.logo', '.footer-brand',
    'deferred-media', 'product-model'
  ].join(',');
  const HARD_OFF = '[data-cn-upload-mask="off"],[data-cn-product-image="false"],[data-cn-product-image="off"]';
  const CONTEXT_ATTRIBUTES = ['data-cn-upload-mask', 'data-cn-product-image', 'data-cn-media-frame',
    'data-image-source', 'data-cn-brand-mask', 'data-cn-brand-mask-asset'];
  const DISCOVERY = 'img[data-cn-upload-mask="white-border"],img[data-cn-product-image],img[data-cn-brand-mask="white-border"],img.cn-product-media-image,img.cn-upload-media-image,' + PRODUCT_SELECTORS;
  const MAGNIFIER = '.cn-native-product .product__media .image-magnify-full-size';
  const white = (r, g, b) => Math.min(r, g, b) >= 252 && Math.max(r, g, b) - Math.min(r, g, b) <= 2;

  function analyzePixels(data, width, height) {
    const total = width * height;
    const skip = reason => ({ state: 'skipped', reason });
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 24 || height < 24 ||
        width > MAX_EDGE || height > MAX_EDGE || data.length !== total * 4) return skip('unsupported-dimensions');
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) return { state: 'already-transparent', reason: 'source-alpha' };
    }
    let border = 0;
    let whiteBorder = 0;
    const background = new Uint8Array(total);
    const queue = new Int32Array(total);
    let read = 0;
    let write = 0;
    const enqueue = index => {
      if (background[index]) return;
      const i = index * 4;
      if (!white(data[i], data[i + 1], data[i + 2])) return;
      background[index] = 1;
      queue[write++] = index;
    };
    const edge = index => {
      border++;
      const i = index * 4;
      if (white(data[i], data[i + 1], data[i + 2])) whiteBorder++;
      enqueue(index);
    };
    for (let x = 0; x < width; x++) { edge(x); edge((height - 1) * width + x); }
    for (let y = 1; y < height - 1; y++) { edge(y * width); edge(y * width + width - 1); }
    if (whiteBorder / border < 0.995) return skip('non-white-border');
    while (read < write) {
      const index = queue[read++];
      const x = index % width;
      if (x) enqueue(index - 1);
      if (x < width - 1) enqueue(index + 1);
      if (index >= width) enqueue(index - width);
      if (index + width < total) enqueue(index + width);
    }
    const retained = total - write;
    const removed = write / total;
    if (removed < 0.08 || retained / total < 0.025) return skip('insufficient-subject');
    let left = width, right = 0, top = height, bottom = 0, dark = 0, light = 0;
    for (let index = 0; index < total; index++) {
      if (background[index]) continue;
      const x = index % width, y = Math.floor(index / width), i = index * 4;
      left = Math.min(left, x); right = Math.max(right, x);
      top = Math.min(top, y); bottom = Math.max(bottom, y);
      const low = Math.min(data[i], data[i + 1], data[i + 2]);
      const high = Math.max(data[i], data[i + 1], data[i + 2]);
      if (data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722 < 180) dark++;
      if (low >= 210) light++;
    }
    if (left <= 0 || top <= 0 || right >= width - 1 || bottom >= height - 1) return skip('subject-touches-edge');
    const boxArea = (right - left + 1) * (bottom - top + 1);
    const fill = retained / boxArea;
    if (fill < 0.15) return skip('uncertain-light-object');
    let largestComponent = retained;
    if (dark / retained < 0.12 || light / retained > 0.55) {
      // A closed pale object can be safe: its enclosed white body must form a
      // dense connected foreground. A broken outline or isolated shadows cannot.
      if (fill < 0.65) return skip('uncertain-light-object');
      largestComponent = 0;
      const visited = new Uint8Array(total);
      for (let first = 0; first < total; first++) {
        if (background[first] || visited[first]) continue;
        let componentRead = 0, componentWrite = 1;
        queue[0] = first; visited[first] = 1;
        const visit = index => {
          if (background[index] || visited[index]) return;
          visited[index] = 1; queue[componentWrite++] = index;
        };
        while (componentRead < componentWrite) {
          const index = queue[componentRead++], x = index % width;
          if (x) visit(index - 1);
          if (x < width - 1) visit(index + 1);
          if (index >= width) visit(index - width);
          if (index + width < total) visit(index + width);
        }
        largestComponent = Math.max(largestComponent, componentWrite);
      }
      if (largestComponent / retained < 0.97) return skip('uncertain-light-object');
    }
    const mask = new Uint8ClampedArray(total * 4);
    for (let index = 0; index < total; index++) {
      const i = index * 4;
      mask[i] = mask[i + 1] = mask[i + 2] = 255;
      if (background[index]) continue;
      let alpha = 255;
      const x = index % width;
      const low = Math.min(data[i], data[i + 1], data[i + 2]);
      const high = Math.max(data[i], data[i + 1], data[i + 2]);
      const meetsBackground = (x > 0 && background[index - 1]) ||
        (x < width - 1 && background[index + 1]) ||
        (index >= width && background[index - width]) ||
        (index + width < total && background[index + width]);
      // Only a single boundary ring may soften. RGB and enclosed whites remain untouched.
      if (meetsBackground && low >= 245 && high - low <= 4) alpha = Math.round(Math.min(1, (255 - low) / 10) * 255);
      mask[i + 3] = alpha;
    }
    return { state: 'applied', reason: 'white-studio-background', mask, width, height, removed,
      bounds: { left, top, right, bottom }, foregroundFill: fill, largestComponentFraction: largestComponent / retained };
  }

  function brandMaskAsset(image) {
    if (!image || image.getAttribute('data-cn-brand-mask') !== 'white-border') return '';
    const source = image.currentSrc || image.getAttribute('src') || '';
    const declared = image.getAttribute('data-cn-brand-mask-asset') || '';
    if (!Object.prototype.hasOwnProperty.call(BRAND_MASK_ASSETS, declared)) return '';
    if (/^data:image\/(?:png|jpe?g);base64,/i.test(source)) return declared;
    try {
      const parsed = new URL(source, 'https://local.invalid/');
      const actual = decodeURIComponent(parsed.pathname.split('/').pop());
      return actual === declared ? declared : '';
    } catch { return ''; }
  }
  function eligibleImage(image) {
    if (!image || image.tagName !== 'IMG') return false;
    if (image.closest(HARD_OFF)) return false;
    // Only a marked IMG may opt a custom upload/logo/poster into the analyzer.
    // Media players, models and unmarked editorial images never enter here.
    if (image.getAttribute('data-cn-upload-mask') === 'white-border') return true;
    // These two explicitly opted-in original logo files are the only exception
    // to the general logo exclusion; the inline-byte identity is checked later.
    if (brandMaskAsset(image)) return !image.closest('[data-image-source="custom"],[data-cn-product-image="false"]');
    if (image.closest(EXCLUDED)) return false;
    const explicit = image.hasAttribute('data-cn-product-image') && image.getAttribute('data-cn-product-image') !== 'auto';
    if (!explicit && !image.matches(PRODUCT_SELECTORS)) return false;
    if (image.matches('.predictive-search__image')) {
      const href = image.closest('a[href]')?.getAttribute('href') || '';
      if (!/\/products\/[^/?#]+/.test(href)) return false;
    }
    const thumb = image.closest('.thumbnail-list__item');
    if (thumb) {
      if (thumb.querySelector('.thumbnail__badge')) return false;
      const gallery = image.closest('media-gallery');
      const target = thumb.getAttribute('data-target');
      const source = gallery && Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]')).find(item => item.getAttribute('data-media-id') === target);
      if (source && !source.querySelector('.media-type-image')) return false;
    }
    const modalThumb = image.closest('[data-cn-modal-index]');
    if (modalThumb) {
      const modal = image.closest('.cn-media-modal');
      const media = modal && Array.from(modal.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      if (media && media[Number(modalThumb.getAttribute('data-cn-modal-index'))]?.tagName !== 'IMG') return false;
    }
    return true;
  }

  function permittedSource(source, origin) {
    if (/^data:image\/(?:png|jpe?g|webp|avif);base64,/i.test(source)) return true;
    try {
      const url = new URL(source, origin);
      if (!/^https?:$/.test(url.protocol)) return false;
      if (/\.svg(?:$|[?#])/i.test(url.pathname)) return false;
      const current = new URL(origin);
      return url.origin === current.origin || url.hostname === 'cdn.shopify.com' ||
        /(?:^|\.)myshopify\.com$/i.test(url.hostname) ||
        /(?:^|\.)shopifycdn\.(?:com|net)$/i.test(url.hostname);
    } catch { return false; }
  }

  // The pure analyzer is available to the isolated Node tests, never as a storefront API.
  if (typeof module === 'object' && module.exports) module.exports = { analyzePixels, eligibleImage, permittedSource, brandMaskAsset, BRAND_MASK_ASSETS, MAX_EDGE, PRODUCT_SELECTORS };
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (window.__cnProductMediaInitialized) return;
  window.__cnProductMediaInitialized = true;
  const records = new WeakMap();
  const frameOwners = new WeakMap();
  const tracked = new Set();
  const cache = new Map();
  const jobs = [];
  const observedMagnifiers = new WeakSet();
  let active = 0;
  let pumping = false;
  let resizeFrame;
  const supportsMask = window.CSS && (CSS.supports('mask-image', 'url("data:image/png;base64,AA==")') || CSS.supports('-webkit-mask-image', 'url("data:image/png;base64,AA==")'));
  const state = (image, value, reason, removed) => {
    image.setAttribute('data-cn-product-mask', value);
    image.setAttribute('data-cn-product-mask-reason', reason);
    if (Number.isFinite(removed)) image.setAttribute('data-cn-product-mask-removed', removed.toFixed(4));
    else image.removeAttribute('data-cn-product-mask-removed');
  };
  const clearMask = image => {
    image.style.removeProperty('--cn-product-mask-image');
    image.style.removeProperty('--cn-product-mask-size');
    image.style.removeProperty('--cn-product-mask-position');
  };
  const carouselVisible = image => !image.closest('.cn-hero-slide[hidden]');
  function syncMagnifier(overlay) {
    if (!overlay.isConnected || !overlay.matches(MAGNIFIER)) return;
    const image = Array.from(overlay.parentElement.children).find(node => node.tagName === 'IMG' && node.matches('.image-magnify-hover'));
    const background = overlay.style.backgroundImage.match(/^url\((["']?)(.*?)\1\)$/);
    let sameSource = false;
    try { sameSource = Boolean(image && background && new URL(background[2], window.location.href).href === image.src); } catch { /* Keep the native original if the source is not comparable. */ }
    const size = overlay.style.backgroundSize;
    if (!sameSource || !size || size === 'auto' || image.getAttribute('data-cn-product-mask') !== 'applied') {
      clearMask(overlay);
      overlay.classList.remove('cn-product-media-magnify');
      state(overlay, 'skipped', sameSource ? 'original-mask-unavailable' : 'magnifier-source-mismatch');
      return;
    }
    const values = {
      '--cn-product-mask-image': image.style.getPropertyValue('--cn-product-mask-image'),
      '--cn-product-mask-size': size,
      '--cn-product-mask-position': overlay.style.backgroundPosition || '0% 0%'
    };
    for (const [key, value] of Object.entries(values)) if (overlay.style.getPropertyValue(key) !== value) overlay.style.setProperty(key, value);
    overlay.classList.add('cn-product-media-magnify');
    state(overlay, 'applied', 'native-magnifier-source-mask');
  }
  function syncImageMagnifiers(image) {
    if (!image.parentElement) return;
    for (const sibling of image.parentElement.children) if (sibling.matches?.(MAGNIFIER)) syncMagnifier(sibling);
  }
  const magnifierObserver = new MutationObserver(changes => {
    const overlays = new Set(changes.map(change => change.target));
    overlays.forEach(syncMagnifier);
  });
  function registerMagnifier(overlay) {
    if (!observedMagnifiers.has(overlay)) {
      observedMagnifiers.add(overlay);
      magnifierObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    }
    syncMagnifier(overlay);
  }
  function frameFor(image) {
    return image.closest('.cn-product-card .card-image,.cn-brand-hero-image[data-image-source="product"],.product-media-container.media-type-image,.thumbnail,.cart-item__image-container,.cn-suggestion-image,.cn-comparison-item-image,.cn-media-thumbnails button');
  }
  function releaseFrames(image, record) {
    for (const { element, className } of record.frames || []) {
      const owners = frameOwners.get(element)?.get(className);
      owners?.delete(image);
      if (!owners?.size) element.classList.remove(className);
    }
    record.frames = [];
  }
  function syncFrames(image, record) {
    const upload = image.getAttribute('data-cn-upload-mask') === 'white-border';
    const className = upload ? 'cn-upload-media-frame' : 'cn-product-media-frame';
    const elements = upload
      ? [image.parentElement?.closest('[data-cn-media-frame]')].filter(Boolean)
      : [...new Set([frameFor(image), image.closest('.product__media-wrapper,.cn-media-viewport')].filter(Boolean))];
    if (record.frames?.length === elements.length &&
        record.frames.every((frame, index) => frame.element === elements[index] && frame.className === className)) return;
    releaseFrames(image, record);
    record.frames = elements.map(element => {
      let classes = frameOwners.get(element);
      if (!classes) { classes = new Map(); frameOwners.set(element, classes); }
      let owners = classes.get(className);
      if (!owners) { owners = new Set(); classes.set(className, owners); }
      owners.add(image);
      element.classList.add(className);
      return { element, className };
    });
  }
  function trimCache() {
    let bytes = 0;
    for (const entry of cache.values()) bytes += entry.bytes;
    for (const [key, entry] of cache) {
      if (cache.size <= CACHE_ENTRIES && bytes <= CACHE_BYTES) break;
      if (!entry.done) continue;
      cache.delete(key); bytes -= entry.bytes;
    }
  }
  function decode(source) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      const timeout = setTimeout(() => { image.onload = image.onerror = null; reject(new Error('image-timeout')); }, 8000);
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => { clearTimeout(timeout); image.onload = image.onerror = null; resolve(image); };
      image.onerror = () => { clearTimeout(timeout); image.onload = image.onerror = null; reject(new Error('image-load-or-cors')); };
      image.src = source;
    });
  }
  async function analyzeSource(source) {
    if (!permittedSource(source, window.location.href)) return { state: 'skipped', reason: 'unsupported-source' };
    try {
      const decoded = await decode(source);
      const scale = Math.min(1, MAX_EDGE / Math.max(decoded.naturalWidth, decoded.naturalHeight));
      const width = Math.round(decoded.naturalWidth * scale), height = Math.round(decoded.naturalHeight * scale);
      if (width < 24 || height < 24) return { state: 'skipped', reason: 'unsupported-dimensions' };
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return { state: 'failed', reason: 'canvas-unavailable' };
      context.drawImage(decoded, 0, 0, width, height);
      const result = analyzePixels(context.getImageData(0, 0, width, height).data, width, height);
      if (result.state === 'applied') {
        const pixels = context.createImageData(width, height);
        pixels.data.set(result.mask);
        context.putImageData(pixels, 0, 0);
        result.url = canvas.toDataURL('image/png');
        if (!result.url.startsWith('data:image/png;')) return { state: 'failed', reason: 'mask-encoding-failed' };
        delete result.mask;
      }
      canvas.width = canvas.height = 0;
      return result;
    } catch (error) {
      return { state: 'failed', reason: error?.name === 'SecurityError' ? 'canvas-readback-blocked' :
        ['image-timeout', 'image-load-or-cors'].includes(error?.message) ? error.message : 'canvas-or-decode-failed' };
    }
  }
  async function verifiedBrandSource(image, source) {
    if (image.getAttribute('data-cn-brand-mask') !== 'white-border') return true;
    const asset = brandMaskAsset(image);
    if (!asset) return false;
    if (!source.startsWith('data:')) return true;
    if (!window.crypto?.subtle || source.length > 200000) return false;
    try {
      const bytes = Uint8Array.from(window.atob(source.split(',')[1]), character => character.charCodeAt(0));
      const digest = await window.crypto.subtle.digest('SHA-256', bytes);
      const hex = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      return hex === BRAND_MASK_ASSETS[asset];
    } catch { return false; }
  }
  function cached(source) {
    let entry = cache.get(source);
    if (entry) { cache.delete(source); cache.set(source, entry); return entry.promise; }
    entry = { done: false, bytes: source.length, promise: null };
    entry.promise = analyzeSource(source).then(result => {
      entry.done = true; entry.bytes += result.url?.length || 0;
      trimCache(); return result;
    });
    cache.set(source, entry);
    trimCache();
    return entry.promise;
  }
  function alignMask(image) {
    const style = window.getComputedStyle(image);
    const fit = style.objectFit || 'fill';
    if (!['contain', 'cover', 'fill'].includes(fit)) return false;
    image.style.setProperty('--cn-product-mask-size', fit === 'fill' ? '100% 100%' : fit);
    image.style.setProperty('--cn-product-mask-position', style.objectPosition || '50% 50%');
    return true;
  }
  async function process(image, record) {
    record.queued = false;
    if (!image.isConnected || !carouselVisible(image)) return;
    if (!eligibleImage(image)) { clearMask(image); state(image, 'skipped', 'excluded-context'); return; }
    const source = image.currentSrc || image.getAttribute('src') || '';
    if (!source || !image.complete || !image.naturalWidth) return;
    if (record.source === source && image.getAttribute('data-cn-product-mask') !== 'pending') { alignMask(image); return; }
    record.source = source;
    const version = record.version;
    clearMask(image);
    state(image, 'pending', 'analysis');
    if (!supportsMask) { state(image, 'skipped', 'mask-not-supported'); return; }
    if (!await verifiedBrandSource(image, source)) { state(image, 'skipped', 'brand-asset-identity-unverified'); return; }
    const existing = window.getComputedStyle(image);
    if (existing.maskImage && existing.maskImage !== 'none' || existing.webkitMaskImage && existing.webkitMaskImage !== 'none') {
      state(image, 'skipped', 'existing-mask'); return;
    }
    const result = await cached(source);
    if (!image.isConnected || record.version !== version || (image.currentSrc || image.getAttribute('src')) !== source || !eligibleImage(image)) return;
    if (result.state === 'applied') {
      if (!alignMask(image)) { state(image, 'skipped', 'unsupported-object-fit'); return; }
      image.style.setProperty('--cn-product-mask-image', 'url("' + result.url + '")');
    }
    state(image, result.state, result.reason, result.removed);
    syncImageMagnifiers(image);
  }
  function pump() {
    if (pumping) return;
    pumping = true;
    const run = () => {
      pumping = false;
      while (active < 2 && jobs.length) {
        const [image, record] = jobs.shift();
        active++;
        process(image, record).catch(() => state(image, 'failed', 'unexpected-analysis-failure')).finally(() => { active--; pump(); });
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 250 });
    else setTimeout(run, 0);
  }
  function schedule(image) {
    const record = records.get(image);
    if (!record || record.queued || !record.visible || !image.isConnected || !carouselVisible(image)) return;
    record.queued = true;
    jobs.push([image, record]);
    pump();
  }
  function reset(image) {
    const record = records.get(image);
    if (!record) return;
    record.version++; record.source = '';
    clearMask(image);
    state(image, 'pending', 'source-changed');
    syncImageMagnifiers(image);
    window.requestAnimationFrame(() => schedule(image));
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const record = records.get(entry.target);
      if (record) { record.visible = entry.isIntersecting; if (record.visible) schedule(entry.target); }
    }
  }, { rootMargin: '200px' }) : null;
  function register(image) {
    if (!eligibleImage(image)) {
      if (records.has(image)) {
        const record = records.get(image);
        record.version++;
        clearMask(image);
        image.classList.remove('cn-product-media-image', 'cn-upload-media-image');
        releaseFrames(image, record);
        observer?.unobserve(image);
        tracked.delete(image);
        state(image, 'skipped', 'excluded-context');
      }
      return;
    }
    const presentation = image.getAttribute('data-cn-upload-mask') === 'white-border'
      ? 'cn-upload-media-image' : 'cn-product-media-image';
    if (records.has(image)) {
      const record = records.get(image);
      if (record.presentation !== presentation || !image.classList.contains(presentation)) {
        record.version++; record.source = '';
        clearMask(image);
        image.classList.remove('cn-product-media-image', 'cn-upload-media-image');
        image.classList.add(presentation);
        state(image, 'pending', 'visibility');
      }
      record.presentation = presentation;
      syncFrames(image, record);
      tracked.add(image);
      if (observer) observer.observe(image);
      else schedule(image);
      return;
    }
    const record = { source: '', version: 0, queued: false, visible: !observer, presentation, frames: [] };
    records.set(image, record); tracked.add(image);
    if (presentation === 'cn-product-media-image' && !brandMaskAsset(image) && !image.hasAttribute('data-cn-product-image')) image.setAttribute('data-cn-product-image', 'auto');
    image.classList.add(presentation);
    syncFrames(image, record);
    clearMask(image);
    state(image, 'pending', 'visibility');
    image.addEventListener('load', () => {
      const source = image.currentSrc || image.getAttribute('src') || '';
      if (record.source && source !== record.source) reset(image);
      schedule(image);
    });
    image.addEventListener('error', () => { record.version++; clearMask(image); state(image, 'failed', 'original-image-error'); syncImageMagnifiers(image); });
    if (observer) observer.observe(image);
    else schedule(image);
  }
  function discover(node, activate = false) {
    if (node.nodeType !== 1 && node.nodeType !== 9) return;
    const visit = image => { register(image); if (activate) schedule(image); };
    if (node.tagName === 'IMG') visit(node);
    if (node.matches?.(MAGNIFIER)) registerMagnifier(node);
    node.querySelectorAll(DISCOVERY).forEach(visit);
    node.querySelectorAll(MAGNIFIER).forEach(registerMagnifier);
  }
  function releaseDisconnected() {
    for (const image of tracked) {
      if (!image.isConnected) { observer?.unobserve(image); releaseFrames(image, records.get(image)); tracked.delete(image); }
    }
  }
  function refreshSources() {
    resizeFrame = null;
    for (const image of tracked) {
      if (!image.isConnected) { observer?.unobserve(image); releaseFrames(image, records.get(image)); tracked.delete(image); continue; }
      const record = records.get(image);
      if (record.source && (image.currentSrc || image.getAttribute('src')) !== record.source) reset(image);
      else if (image.getAttribute('data-cn-product-mask') === 'applied') alignMask(image);
      else schedule(image);
    }
  }
  function start() {
    discover(document);
    new MutationObserver(changes => {
      let removedElements = false;
      const contexts = new Set();
      for (const change of changes) {
        if (change.type === 'childList') {
          change.addedNodes.forEach(node => discover(node));
          if (!removedElements) removedElements = Array.from(change.removedNodes).some(node => node.nodeType === 1);
        } else if (CONTEXT_ATTRIBUTES.includes(change.attributeName)) {
          contexts.add(change.target);
        } else if (change.attributeName === 'hidden') {
          if (change.target.matches('.cn-hero-slide') && !change.target.hidden) discover(change.target, true);
        } else if (change.target.tagName === 'IMG') {
          if (records.has(change.target)) reset(change.target);
          else register(change.target);
        } else if (change.target.tagName === 'SOURCE') {
          change.target.closest('picture')?.querySelectorAll('img').forEach(image => records.has(image) ? reset(image) : register(image));
        }
      }
      contexts.forEach(node => discover(node));
      if (removedElements) releaseDisconnected();
    }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'sizes', 'hidden', ...CONTEXT_ATTRIBUTES] });
    window.addEventListener('resize', () => { if (!resizeFrame) resizeFrame = window.requestAnimationFrame(refreshSources); }, { passive: true });
    window.addEventListener('pageshow', refreshSources);
    document.addEventListener('shopify:section:load', event => discover(event.target));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
